// Historial de descargas de la cartera de clientes.
//
// Cada exportación a Excel o CSV ya quedaba anotada en el registro de actividad
// (`clients.exported`) desde hace tiempo, pero no había forma de verla desde la
// aplicación, así que en la práctica no servía para nada: la pregunta "¿quién se
// llevó la lista de clientes y cuándo?" solo se podía responder entrando a la
// base de datos (Lissette 2026-08-14).
//
// Es la lista completa de las fichas de la agencia — nombre, teléfono, dirección,
// fecha de nacimiento, ingresos, pólizas y notas — así que saber quién la
// descargó no es una curiosidad: es lo que HIPAA §164.312(b) espera que exista.
//
// La dueña de la agencia ve todas las descargas; los demás roles ven las suyas.
// La ventana lo dice, para que nadie crea que está viendo el registro entero
// cuando no lo está.
import { useEffect, useState } from 'react';
import { History, ShieldCheck } from 'lucide-react';
import { Modal } from './ui.jsx';
import { getClientExportHistory } from '../api.js';
import { IS_DEMO } from '../demoMode.js';

// Fecha y hora en formato de EE. UU. y en hora LOCAL. `toISOString()` correría
// el día en cualquier zona al oeste de UTC — una descarga de las 8 de la noche
// aparecería como del día siguiente.
function fmtStamp(iso, es) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }) + (es ? '' : '');
}

export default function ClientExportHistoryModal({ lang = 'es', onClose }) {
  const es = lang === 'es';
  const [rows, setRows] = useState(null);
  const [scope, setScope] = useState('mine');
  const [error, setError] = useState(null);

  useEffect(() => {
    // La demo pública no tiene servidor: apiFetch rechaza a propósito, y sin
    // esta salida la ventana se abriría con un error rojo en la cara del
    // visitante. Un historial vacío es la verdad allí.
    if (IS_DEMO) { setRows([]); return undefined; }
    let alive = true;
    getClientExportHistory()
      .then((r) => { if (alive) { setRows(r.data); setScope(r.scope); } })
      .catch((e) => { if (alive) setError(e.message || String(e)); });
    return () => { alive = false; };
  }, []);

  const cell = { padding: '.5rem .6rem', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', borderBottom: '1px solid var(--c-border,#1a3550)' };
  const head = { ...cell, fontSize: '.64rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 };

  return (
    <Modal title={es ? 'Historial de exportaciones' : 'Export history'} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
        <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.55, display: 'flex', gap: '.55rem', alignItems: 'flex-start' }}>
          <ShieldCheck size={16} style={{ color: 'var(--c-cyan,#00d4ff)', flexShrink: 0, marginTop: 2 }} />
          <span>
            {es
              ? 'Cada descarga de la lista de clientes queda registrada con quién la hizo, cuándo y cuántas fichas se llevó.'
              : 'Every download of the client list is recorded with who did it, when, and how many records left.'}
            {' '}
            {scope === 'agency'
              ? (es ? 'Estás viendo las de toda la agencia.' : 'You are seeing the whole agency.')
              : (es ? 'Estás viendo solo las tuyas.' : 'You are seeing only your own.')}
          </span>
        </div>

        {error && (
          <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'var(--c-red-dim)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem' }}>{error}</div>
        )}

        {rows === null && !error && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1.5rem' }}>{es ? 'Cargando…' : 'Loading…'}</div>
        )}

        {rows && rows.length === 0 && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.5rem' }}>
            <History size={22} style={{ opacity: .6 }} />
            {es ? 'Todavía no se ha exportado la lista de clientes.' : 'The client list has not been exported yet.'}
          </div>
        )}

        {rows && rows.length > 0 && (
          <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Fecha y hora' : 'Date and time'}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Quién' : 'Who'}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{es ? 'Fichas' : 'Records'}</th>
                  <th style={{ ...head, textAlign: 'left' }}>{es ? 'Formato' : 'Format'}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...cell, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: '.72rem' }}>{fmtStamp(r.at, es)}</td>
                    <td style={cell}>{r.by || (es ? '—' : '—')}</td>
                    <td style={{ ...cell, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {r.rows == null ? '—' : r.rows}
                      {/* Las descargas anteriores a este cambio no anotaban cuántas
                          filas escribió la pantalla, solo cuántas entregó el
                          servidor; se marcan para no leerlas como exactas. */}
                      {r.rows != null && r.available != null && r.rows !== r.available && (
                        <span style={{ color: 'var(--c-text3,#45688a)', fontSize: '.66rem' }}> / {r.available}</span>
                      )}
                    </td>
                    <td style={{ ...cell, textTransform: 'uppercase', fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)' }}>{r.format || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows && rows.length > 0 && (
          <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
            {es
              ? 'La columna «Fichas» muestra las filas descargadas y, cuando difiere, las que había disponibles.'
              : 'The “Records” column shows the rows downloaded and, when different, how many were available.'}
          </div>
        )}
      </div>
    </Modal>
  );
}
